// TASK-901 — build a rebalancing proposal for a client, on demand.
//
// Generated rather than read. Schneider had a stored proposal and Ammann had none, so a
// read-only view would have been empty for the very client the defect was reported against —
// and "the canvas cannot reach it" would have remained true for everyone else.
//
// Two steps, because the target slot is not in the prompt: read the client's drift, pick the
// slot the proposal should address, then size it. `worstDriftedSlot` is the pure chooser, so the
// widget, this hook and the tests all agree on which slot that is.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useDrift } from './useDrift';
import {
  rebalanceService,
  worstDriftedSlot,
  type DriftSnapshot,
  type RebalanceProposal,
} from '../services/rebalanceService';

/** Shape `useDrift` returns into the `{ by_slot }` snapshot `worstDriftedSlot` reads.
 *
 *  The drift endpoint and the proposal engine describe the same slots with different keys;
 *  normalising here keeps that seam in one place instead of in the chooser. */
type RawSlot = {
  current?: number; target?: number; drift?: number; breach?: boolean;
  status?: string; asset_class?: string;
};
type RawDrift = {
  drift_by_asset_class?: Record<string, RawSlot>;
  total_portfolio_value?: number;
  max_drift_pct?: number;
};

export function driftToSnapshot(data: unknown): DriftSnapshot | null {
  // `getDrift` returns `{ client_id, portfolio_id, drift: {...} }` — the slots are one level
  // down. Both shapes are accepted so a caller holding the inner object (or a test fixture
  // written against it) is not silently treated as "no drift", which would render "nothing is
  // outside its band" over a portfolio in breach.
  const outer = data as ({ drift?: RawDrift } & RawDrift) | null;
  const d: RawDrift | null | undefined = outer?.drift ?? outer;
  const raw = d?.drift_by_asset_class;
  if (!raw) return null;
  const by_slot: DriftSnapshot['by_slot'] = {};
  for (const [slot, m] of Object.entries(raw)) {
    by_slot[slot] = {
      current: m.current ?? 0,
      target: m.target ?? 0,
      drift: m.drift ?? 0,
      // `breach` when present; otherwise derived from the status the endpoint sends, the same
      // way DriftBars does it ('on_target' ⇔ in band).
      breach: m.breach ?? (m.status !== undefined ? m.status !== 'on_target' : false),
      asset_class: m.asset_class ?? '',
    };
  }
  return {
    by_slot,
    max_drift_pct: d?.max_drift_pct ?? 0,
    total_portfolio_value: d?.total_portfolio_value ?? 0,
  };
}

export function useRebalanceProposal(clientId: string, slotOverride?: string) {
  const drift = useDrift(clientId);
  const slot = slotOverride ?? worstDriftedSlot(driftToSnapshot(drift.data));

  const query = useQuery({
    queryKey: ['rebalance-proposal', clientId, slot],
    queryFn: () => rebalanceService.build(clientId, slot as string),
    // No slot means nothing is out of band. Firing anyway would ask the engine to size a trade
    // with no reason behind it.
    enabled: !!clientId && !!slot,
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    slot,
    // Drift has to load before a slot can be chosen; surfacing its state separately would show
    // "no proposal" for the moment before the choice is possible.
    isLoading: drift.isLoading || (!!slot && query.isLoading),
    isError: drift.isError || query.isError,
  };
}

export function useSaveProposal(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proposal: RebalanceProposal) => rebalanceService.save(clientId, proposal),
    onSuccess: () => {
      // The saved list is what "visible on reload" means (TASK-901 AC 6).
      qc.invalidateQueries({ queryKey: ['portfolio-proposals', clientId] });
    },
  });
}

export function useSavedProposals(clientId: string) {
  return useQuery({
    queryKey: ['portfolio-proposals', clientId],
    queryFn: () => rebalanceService.list(clientId),
    enabled: !!clientId,
  });
}
